import React, { useEffect, useRef, useState } from 'react';
import { getToken } from '../services/api';

/**
 * Player for visit recordings.
 *
 * Two quirks it exists to work around:
 *
 *  1. The /api/.../recording endpoints sit behind JwtAuthGuard, and a bare
 *     <audio src="..."> cannot send an Authorization header. The element got a
 *     401 back and rendered as a dead "0:00 / 0:00" control. So fetch the bytes
 *     ourselves with the token and hand the element a blob URL instead.
 *
 *  2. MediaRecorder writes WebM with no Duration element, so browsers report
 *     duration as Infinity until the stream has been seeked to the end once.
 *     Doing that on loadedmetadata makes the real length show up.
 */
export default function RecordingPlayer({
    src,
    className,
    style,
}: {
    src: string;
    className?: string;
    style?: React.CSSProperties;
}) {
    const [url, setUrl] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    useEffect(() => {
        let cancelled = false;
        let objectUrl: string | null = null;
        setUrl(null);
        setError(null);

        (async () => {
            try {
                const token = getToken();
                const res = await fetch(src, {
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                });
                if (!res.ok) {
                    // The endpoint distinguishes "never uploaded" from "uploaded but
                    // the file is gone" — pass its reason through rather than guessing.
                    let reason = '';
                    try { reason = (await res.json())?.message || ''; } catch { /* not JSON */ }
                    throw new Error(reason || `Could not load the recording (HTTP ${res.status})`);
                }
                const blob = await res.blob();
                if (cancelled) return;
                if (!blob.size) throw new Error('The recording file is empty');
                objectUrl = URL.createObjectURL(blob);
                setUrl(objectUrl);
            } catch (e: any) {
                if (!cancelled) setError(e?.message || 'Could not load the recording');
            }
        })();

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [src]);

    // Force the browser to resolve a duration the WebM header never carried.
    const resolveDuration = () => {
        const el = audioRef.current;
        if (!el || el.duration !== Infinity) return;
        const settle = () => {
            el.removeEventListener('timeupdate', settle);
            el.currentTime = 0;
        };
        el.addEventListener('timeupdate', settle);
        el.currentTime = 1e101;
    };

    if (error) {
        return <p className={`text-[11px] text-red-600 font-medium ${className || ''}`}>{error}</p>;
    }

    if (!url) {
        return <p className={`text-[11px] text-gray-400 ${className || ''}`}>Loading recording…</p>;
    }

    return (
        <audio
            ref={audioRef}
            controls
            preload="metadata"
            src={url}
            className={className}
            style={style}
            onLoadedMetadata={resolveDuration}
            onDurationChange={resolveDuration}
        >
            Your browser does not support audio playback.
        </audio>
    );
}
